'use client';

import React from 'react';
import { clsx } from 'clsx';
import {
  GROW_DURATION_MS,
  GROW_EASE,
  GROW_STAGGER_STEP_MS,
  useGrowOnView,
} from '../chatgpt-dental-citations/useGrowOnView';
import { FEATURES, FEATURE_SCALE_MAX_PCT, RISK_DIFF_CI_PP, barWidthPct } from './data';
import styles from './case-control.module.css';

/**
 * The six-row cited-vs-uncited paired-bar chart (HAL-188): the page's one
 * motion moment. Each row is one feature with two 14px tracks on a shared
 * 0–40% scale – cited above, matched uncited below – and the odds ratio and
 * p-value at the right, so a screen reader hears label → cited value →
 * uncited value → test while the bars themselves are `aria-hidden`.
 *
 * Colour carries one thing: a cited bar is amber only where the matched test
 * held (long content, the composite); every other cited bar is midnight and
 * every uncited bar is grey. Significance is also stated in the test line, so
 * it never rests on colour alone.
 *
 * Motion is the shared `useGrowOnView` contract from the sibling dental page:
 * bars grow from 0 on first scroll-into-view, 280ms, staggered 70ms per row.
 * The two bars in a row share one delay so the gap between them appears as a
 * single event. Reduced-motion, no-observer and no-JS output renders complete.
 */
export function PairedComparisonChart(): React.ReactElement {
  const { ref, armed, grown } = useGrowOnView<HTMLDivElement>();
  const settled = !armed || grown;

  const barStyle = (valuePct: number, index: number): React.CSSProperties => ({
    width: settled ? barWidthPct(valuePct) : '0%',
    transition: `width ${GROW_DURATION_MS}ms ${GROW_EASE}`,
    transitionDelay: armed && grown ? `${index * GROW_STAGGER_STEP_MS}ms` : '0ms',
  });

  return (
    <div
      ref={ref}
      className={styles.chart}
      role="group"
      aria-label="Share of sites carrying each feature, cited versus matched uncited"
    >
      <div className={styles.legend}>
        <span>
          <i className={styles.swatchSignificant} />
          Cited, difference held after matching
        </span>
        <span>
          <i className={styles.swatchCited} />
          Cited, no significant difference
        </span>
        <span>
          <i />
          Matched uncited
        </span>
      </div>
      <div className={styles.head} aria-hidden="true">
        <div>Feature</div>
        <div>Share of sites with it</div>
        <div className={styles.values}>
          <span>Cited</span>
          <span>Uncited</span>
        </div>
      </div>
      <div className={styles.axis} aria-hidden="true">
        <div />
        <div className={styles.ticks}>
          <span>0%</span>
          <span style={{ left: '25%' }}>10</span>
          <span style={{ left: '50%' }}>20</span>
          <span style={{ left: '75%' }}>30</span>
          <span style={{ left: '100%' }}>{FEATURE_SCALE_MAX_PCT}%</span>
        </div>
        <div />
      </div>
      <dl className={styles.rows}>
        {FEATURES.map((row, index) => (
          <div
            key={row.key}
            className={clsx(
              styles.row,
              row.significant && styles.significant,
              row.composite && styles.composite
            )}
            data-feature={row.key}
            data-significant={row.significant ? 'true' : undefined}
          >
              <dt>
                {row.label}
                <small>{row.definition}</small>
              </dt>
              <dd className={styles.tracks} aria-hidden="true">
                <i className={styles.gridline} />
                <div className={clsx(styles.track, styles.cited)}>
                  <span style={barStyle(row.citedPct, index)} />
                </div>
                <div className={styles.track}>
                  <span style={barStyle(row.uncitedPct, index)} />
                </div>
              </dd>
              <dd className={styles.values}>
                <div className={styles.numbers}>
                  <b>{row.citedPct.toFixed(1)}%</b>
                  <span>{row.uncitedPct.toFixed(1)}%</span>
                </div>
                <span className={styles.stat}>{row.stat}</span>
              </dd>
            </div>
        ))}
      </dl>
      <p className={styles.caption}>
        Shared scale, 0–{FEATURE_SCALE_MAX_PCT}%. Odds ratios and p-values are
        Cochran–Mantel–Haenszel, stratified on metro × area of dentistry; risk differences in
        percentage points (95% CI for long content {RISK_DIFF_CI_PP.L3_long_content[0]} to{' '}
        {RISK_DIFF_CI_PP.L3_long_content[1]}; for the composite {RISK_DIFF_CI_PP.ANY[0]} to{' '}
        {RISK_DIFF_CI_PP.ANY[1]}). &ldquo;Not significant&rdquo; means the matched test did not
        reach p &lt; .05.
      </p>
    </div>
  );
}
